<template>
    <BaseDialog
        title="New Testimonial"
        v-model:visible="visible"
        :save-action="create"
        :okLabel="testimonialId ? 'Update' : 'Save'"
        :okDisabled="canSave"
        :loading="uploading"
    >
        <template v-slot:content>
            <form @submit.prevent="submitForm" class="flex flex-col gap-4">
                <Divider align="left" type="solid" class="!my-0">
                    <b>General Info</b>
                </Divider>

                <FormItem itemLabel="Title" :isInvalid="v$.title.$error" :errorMessage="v$.title.required.$message">
                    <template v-slot:content>
                        <InputText
                            v-model="form.title"
                            class="w-full"
                            autocomplete="off"
                            :class="{ 'p-invalid': v$.title.$error }"
                        />
                    </template>
                </FormItem>

                <FormItem
                    itemLabel="Description"
                    :isInvalid="v$.description.$error"
                    :errorMessage="v$.description.required.$message"
                >
                    <template v-slot:content>
                        <Textarea
                            v-model="form.description"
                            :class="{ 'p-invalid': v$.description.$error }"
                            class="w-full"
                            rows="5"
                            cols="30"
                            style="resize: none"
                        />
                    </template>
                </FormItem>

                <div class="grid grid-cols-2 gap-4 w-full">
                    <FormItem itemLabel="Author">
                        <template v-slot:content>
                            <AuthorDropdown
                                :selectedAuthor="form.author"
                                @selectedAuthor="onAuthorSelected"
                            ></AuthorDropdown>
                        </template>
                    </FormItem>

                    <FormItem itemLabel="Visibility">
                        <template v-slot:content>
                            <Select
                                v-model="form.visibility"
                                :options="TestimonialVisibility.getAllOptions()"
                                optionLabel="label"
                                placeholder=" "
                                class="w-full"
                            ></Select>
                        </template>
                    </FormItem>

                    <FormItem itemLabel="Language">
                        <template v-slot:content>
                            <LozalizationDropdown
                                :type="LocalizableType.language"
                                :selection="form.language"
                                @selectedValue="onSelectedCountry"
                            ></LozalizationDropdown>
                        </template>
                    </FormItem>

                    <FormItem itemLabel="Industry">
                        <template v-slot:content>
                            <OptionsMultiSelection
                                :optionType="OptionType.industry"
                                v-model:selection="form.industry"
                            ></OptionsMultiSelection>
                        </template>
                    </FormItem>

                    <FormItem
                        itemLabel="Products"
                        :isInvalid="v$.products.$error"
                        :errorMessage="v$.products.required.$message"
                    >
                        <template v-slot:content>
                            <OptionsMultiSelection
                                :class="{ 'p-invalid': v$.products.$error }"
                                :optionType="OptionType.product"
                                v-model:selection="form.products"
                            ></OptionsMultiSelection>
                        </template>
                    </FormItem>
                </div>

                <Divider align="left" type="solid" class="!my-0">
                    <b>Tags</b>
                </Divider>

                <div class="flex flex-col gap-2">
                    <FormItem itemLabel="Tag">
                        <template v-slot:content>
                            <InputText class="w-full" @keypress="onAddTag" />
                        </template>
                    </FormItem>
                    <div class="card flex flex-wrap gap-2 !mb-0 min-h-[93px]">
                        <Chip v-for="(item, index) in tagList" :key="index" :label="item" removable />
                    </div>
                </div>

                <Divider v-if="!testimonialId" align="left" type="solid" class="!my-0">
                    <b>Video info</b>
                </Divider>

                <FileUpload
                    v-if="!testimonialId"
                    class="!self-start"
                    mode="basic"
                    @select="onFileSelect"
                    customUpload
                    accept="video/*"
                    @upload="onUpload"
                />
            </form>
        </template>
    </BaseDialog>
</template>

<script setup>
import BaseDialog from "@/shared/components/BaseDialog.vue";
import FormItem from "@/shared/components/FormItem.vue";
import Textarea from "primevue/textarea";
import { Select, InputText, Chip, FileUpload } from "primevue";
import Divider from "primevue/divider";
import { defineProps, defineEmits, ref, inject, reactive, computed, onMounted, defineModel } from "vue";
import LozalizationDropdown from "@/shared/common/LozalizationDropdown.vue";
import { useToast } from "primevue/usetoast";
import AuthorDropdown from "@/shared/common/AuthorDropdown.vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, email } from "@vuelidate/validators";
import { TestimonialVisibility } from "@/core/enums/TestimonialVisibilityEnum";
import { LocalizableType } from "@/core/enums/LocalizableEnum";
import OptionsMultiSelection from "@/shared/common/OptionsMultiSelection.vue";
import { OptionType } from "@/core/enums/OptionTypeEnum";

const emit = defineEmits(["testimonialUpdated"]);

const visible = defineModel("visible");

const testimonialService = inject("testimonialService");
const toast = useToast();

const tagList = ref([]);
const fileToUpload = ref();
const uploading = ref(false);

const form = reactive({
    title: "",
    description: "",
    author: null,
    visibility: TestimonialVisibility.private,
    videoUrl: "",
    orientation: null,
    language: null,
    industry: [],
    products: [],
});

const rules = {
    title: { required: helpers.withMessage("The title is required.", required) },
    description: { required: helpers.withMessage("Description is required.", required) },
    products: { required: helpers.withMessage("At least one product is required", required) },
};

const v$ = useVuelidate(rules, form);

const props = defineProps({
    testimonialId: {
        type: String,
        default: null,
    },
});

onMounted(async () => {
    if (!props.testimonialId) {
        return;
    }

    await loadTestimonial();
});

const onFileSelect = (event) => {
    const file = event.files[0];
    fileToUpload.value = file;
};

const canSave = computed(() => {
    if (props.testimonialId) {
        return uploading.value;
    }

    return !fileToUpload.value || uploading.value;
});

const create = async () => {
    try {
        if (await v$.value.$validate()) {
            uploading.value = true;
            const payload = {
                title: form.title,
                description: form.description,
                author: form.author?.value ?? null,
                visibility: form.visibility.value,
                tags: tagList.value,
                products: form.products.map((item) => item.value),
                industry: form.industry.map((item) => item.value),
                language: form.language?.value ?? null,
            };

            if (props.testimonialId) {
                const response = await testimonialService.update(props.testimonialId, payload);

                emit("testimonialUpdated", response.id);
                toast.add({ severity: "success", summary: "Testimonial Updated", life: 3000 });
            } else {
                const response = await testimonialService.create(payload, fileToUpload.value);
                toast.add({ severity: "success", summary: "Testimonial Created", life: 3000 });
            }

            emit("update:visible", false);
        }
    } catch (error) {
        console.log(error);
    } finally {
        uploading.value = false;
    }
};

const updateVisible = (newVisibilityValue) => {
    emit("update:visible", newVisibilityValue);
};

const onAuthorSelected = (author) => {
    form.author = author;
};

const onSelectedCountry = (country) => {
    form.language = country;
};

const onAddTag = (event) => {
    if (event.code == "Enter" && event.target.value != "") {
        tagList.value.push(event.target.value);
        event.target.value = "";
    }
};

const loadTestimonial = async () => {
    const detail = await testimonialService.getDetails(props.testimonialId);

    form.title = detail.title;
    form.description = detail.description;
    form.visibility = TestimonialVisibility[detail.visibility];

    tagList.value = detail.tags;

    if (detail.author) {
        form.author = { value: detail.author.id, label: detail.displayName() };
    }

    if (detail.industries) {
        form.industry = detail.industries.map((item) => {
            return { label: item.name, value: item.id };
        });
    }

    if (detail.products) {
        form.products = detail.products.map((item) => {
            return { label: item.name, value: item.id };
        });
    }

    if (detail.language) {
        form.language = detail.language;
    }
};
</script>

<style scoped>
.p-fileupload-basic {
    justify-content: unset;
}
</style>
